import { useEffect, useState, useContext } from 'react';
import { LoadingContext } from '../App';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Canvas from '../components/Canvas';
import MainFooter from '../components/MainFooter';
import blogData from '../data/blog.json';
import '../styles/blog-detail.css';

const BlogDetail = () => {
    const { slug } = useParams<{ slug: string }>();
    const location = useLocation();
    const [blog, setBlog] = useState<any>(null);
    const { setIsPageReady } = useContext(LoadingContext);

    useEffect(() => {
        const foundBlog = blogData.find(b => b.slug === slug);
        if (foundBlog) {
            setBlog(foundBlog);
            // Signal that the page is ready once the blog data is loaded
            setIsPageReady(true);
        } else {
            // Signal ready even if not found
            setIsPageReady(true);
        }
    }, [slug, setIsPageReady]);

    useEffect(() => {
        // Add class to body for CSS targeting
        document.body.classList.add('blog-detail-page');

        return () => {
            document.body.classList.remove('blog-detail-page');
        };
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    if (!blog) {
        return <div className="loading-screen" style={{ backgroundColor: '#fbfbfd', color: '#1d1d1f', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading Blog...</div>;
    }

    // Enhanced renderer to handle markdown bold and links
    const renderContent = (content: string) => {
        const blocks = content.split('\n\n');

        const renderMarkdown = (text: string) => {
            if (!text) return text;

            // Pattern to match links: [text](url)
            const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
            let parts: (string | JSX.Element)[] = [];
            let lastIndex = 0;
            let match;

            while ((match = linkRegex.exec(text)) !== null) {
                // Add text before the match
                const before = text.substring(lastIndex, match.index);
                if (before) parts.push(...renderBold(before));

                const linkText = match[1];
                const url = match[2];

                if (url.startsWith('/')) {
                    parts.push(<Link key={match.index} to={url} className="markdown-link">{linkText}</Link>);
                } else if (url === '#') {
                    parts.push(<span key={match.index} className="current-post-link">{linkText}</span>);
                } else {
                    parts.push(<a key={match.index} href={url} target="_blank" rel="noopener noreferrer" className="markdown-link">{linkText} <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: '0.7em' }}></i></a>);
                }
                lastIndex = linkRegex.lastIndex;
            }

            // Add remaining text
            const remaining = text.substring(lastIndex);
            if (remaining) parts.push(...renderBold(remaining));

            return parts.length > 0 ? parts : text;
        };

        const renderBold = (text: string) => {
            if (!text.includes('**')) return [text];
            const parts = text.split('**');
            return parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
        };

        const getIcon = (text: string) => {
            const lower = text.toLowerCase();
            if (lower.includes('transparency') || lower.includes('lawfulness')) return 'fa-scale-balanced';
            if (lower.includes('purpose')) return 'fa-bullseye';
            if (lower.includes('minimization')) return 'fa-filter';
            if (lower.includes('accuracy')) return 'fa-check-double';
            if (lower.includes('storage')) return 'fa-database';
            if (lower.includes('integrity') || lower.includes('confidentiality') || lower.includes('security') || lower.includes('network') || lower.includes('firewall')) return 'fa-shield-halved';
            if (lower.includes('accountability') || lower.includes('audit')) return 'fa-user-check';
            if (lower.includes('right') || lower.includes('access') || lower.includes('control')) return 'fa-user-shield';
            if (lower.includes('consent') || lower.includes('valid') || lower.includes('policy')) return 'fa-file-signature';
            if (lower.includes('breach') || lower.includes('notification') || lower.includes('vulnerability') || lower.includes('phishing')) return 'fa-triangle-exclamation';
            if (lower.includes('dpia') || lower.includes('assess') || lower.includes('monitor')) return 'fa-magnifying-glass-chart';
            if (lower.includes('officer') || lower.includes('dpo') || lower.includes('people') || lower.includes('organizational')) return 'fa-user-tie';
            if (lower.includes('erasure') || lower.includes('forgotten')) return 'fa-trash-can';
            if (lower.includes('portability')) return 'fa-file-export';
            if (lower.includes('object')) return 'fa-hand-dots';
            if (lower.includes('ai') || lower.includes('technological') || lower.includes('technologies')) return 'fa-microchip';
            if (lower.includes('medical') || lower.includes('health')) return 'fa-file-medical';
            if (lower.includes('card') || lower.includes('payment') || lower.includes('fintech')) return 'fa-credit-card';
            return 'fa-circle-info';
        };

        return blocks.map((block, index) => {
            // Check if it's a Related Reads block (often starts with Related Reads or contains multiple blog links)
            if (block.toLowerCase().includes('related reads') ||
                (block.includes('[/our-work/blog/') && block.split('\n').length > 2)) {
                const lines = block.split('\n');
                return (
                    <div key={index} className="related-reads-section">
                        {lines[0].trim() && <h4 className="related-reads-title">{renderMarkdown(lines[0])}</h4>}
                        <div className="related-reads-list">
                            {lines.slice(1).map((line, i) => {
                                if (!line.trim()) return null;
                                // Remove bullet points or numbers from the start for cleaner UI
                                const cleanLine = line.replace(/^([-*]|(\d+\.))\s+/, '');
                                return (
                                    <div key={i} className="related-read-item">
                                        <i className="fa-solid fa-chevron-right"></i>
                                        {renderMarkdown(cleanLine)}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            }

            // Headers (Single line or multi-line block starting with header)
            if (block.startsWith('### ')) {
                if (!block.includes('\n')) {
                    return <h3 key={index}>{renderMarkdown(block.replace('### ', '').trim())}</h3>;
                }
            }

            // Multi-line Content Blocks (Headers + Paragraphs + Lists)
            if (block.includes('\n')) {
                const lines = block.split('\n');
                const result: JSX.Element[] = [];
                let currentItems: { title: string; desc: string }[] = [];
                let currentGroupTitle = '';

                const flushItems = (key: string) => {
                    if (currentItems.length > 0) {
                        result.push(
                            <div key={key} className="requirement-section">
                                {currentGroupTitle && <h4 className="requirement-group-title">{currentGroupTitle}</h4>}
                                <div className="requirement-grid">
                                    {currentItems.map((item, i) => (
                                        <div key={i} className="requirement-card">
                                            <div className="requirement-icon">
                                                <i className={`fa-solid ${getIcon(item.title + item.desc)}`}></i>
                                            </div>
                                            <div className="requirement-info">
                                                <h5>{renderMarkdown(item.title)}</h5>
                                                <p>{renderMarkdown(item.desc)}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                        currentItems = [];
                        currentGroupTitle = '';
                    }
                };

                lines.forEach((line, i) => {
                    const numberedMatch = line.match(/^(\d+\.)\s\*\*(.*?)\*\*[:\s—]*(.*)/);
                    const bulletMatch = line.match(/^[-]\s\*\*(.*?)\*\*[:\s—]*(.*)/);
                    const plainNumberedMatch = line.match(/^(\d+\.)\s(.*?):(.*)/);
                    const simpleNumberedMatch = line.match(/^(\d+\.)\s(.*)/);
                    const groupTitleMatch = line.match(/^\*\*(.*?)\*\*$/);
                    const h3Match = line.match(/^### (.*)/);
                    const h4Match = line.match(/^#### (.*)/);

                    // Skip markdown links from being treated as requirement cards
                    const isLinkLine = line.trim().startsWith('- [') || line.trim().match(/^\d+\.\s+\[/);

                    if (h4Match) {
                        flushItems(`h4-${i}`);
                        result.push(<h4 key={`h4-${i}`} className="inner-block-h4">{renderMarkdown(h4Match[1])}</h4>);
                    } else if (h3Match) {
                        flushItems(`head-${i}`);
                        result.push(<h3 key={`h3-${i}`} className="inner-block-header">{renderMarkdown(h3Match[1])}</h3>);
                    } else if (groupTitleMatch && !line.includes('1.') && !line.includes('- ')) {
                        flushItems(`group-${i}`);
                        currentGroupTitle = groupTitleMatch[1];
                    } else if (numberedMatch) {
                        currentItems.push({ title: numberedMatch[2], desc: numberedMatch[3].trim() });
                    } else if (bulletMatch) {
                        currentItems.push({ title: bulletMatch[1], desc: bulletMatch[2].trim() });
                    } else if (plainNumberedMatch && !isLinkLine) {
                        currentItems.push({ title: plainNumberedMatch[2].trim(), desc: plainNumberedMatch[3].trim() });
                    } else if (simpleNumberedMatch && !isLinkLine) {
                        currentItems.push({ title: `Requirement ${simpleNumberedMatch[1]}`, desc: simpleNumberedMatch[2].trim() });
                    } else if (line.trim() !== '') {
                        // Check if it's a title:desc format
                        const parts = line.split(':');
                        if (!isLinkLine && parts.length > 1 && parts[0].length < 60 && (parts[0].includes('**') || parts[0].length < 30)) {
                            currentItems.push({ title: parts[0].replace(/\*\*/g, '').trim(), desc: parts.slice(1).join(':').trim() });
                        } else {
                            if (currentItems.length > 0) {
                                flushItems(`mid-${i}`);
                            }
                            result.push(<p key={`text-${i}`} className="inner-block-text">{renderMarkdown(line)}</p>);
                        }
                    }
                });
                flushItems(`final-${index}`);
                return <div key={index} className="requirements-container">{result}</div>;
            }

            // Paragraphs and Bold Text
            return <p key={index}>{renderMarkdown(block)}</p>;
        });
    };

    return (
        <div className="blog-detail-page">
            <Helmet>
                <title>{`${blog.title} | Sacesta Blog`}</title>
                <meta name="description" content={blog.meta?.description || blog.excerpt || blog.title} />
                {blog.meta?.keywords && <meta name="keywords" content={blog.meta.keywords} />}
                <link rel="canonical" href={`https://sacesta.com/our-work/blog/${slug}`} />

                {/* JSON-LD Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "BlogPosting",
                        "headline": blog.title,
                        "image": `https://sacesta.com/${blog.thumbnail.image}`,
                        "datePublished": blog.date,
                        "author": {
                            "@type": "Organization",
                            "name": "Sacesta Technologies"
                        },
                        "publisher": {
                            "@type": "Organization",
                            "name": "Sacesta Technologies",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://sacesta.com/assets/imgs/logo/site-logo-white-2.png"
                            }
                        },
                        "description": blog.meta?.description || blog.excerpt
                    })}
                </script>

                {/* Open Graph / Facebook / WhatsApp */}
                <meta property="og:type" content="article" />
                <meta property="og:url" content={`https://sacesta.com/our-work/blog/${slug}`} />
                <meta property="og:title" content={blog.title} />
                <meta property="og:description" content={blog.meta?.description || blog.excerpt} />
                <meta property="og:image" content={`https://sacesta.com/${blog.thumbnail.image}`} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content={`https://sacesta.com/our-work/blog/${slug}`} />
                <meta name="twitter:title" content={blog.title} />
                <meta name="twitter:description" content={blog.meta?.description || blog.excerpt} />
                <meta name="twitter:image" content={`https://sacesta.com/${blog.thumbnail.image}`} />
            </Helmet>
            <div className="cursor1"></div>
            <div className="cursor2"></div>

            <div className="has-smooth" id="has_smooth"></div>

            <Canvas />

            <button id="scroll_top" className="scroll-top"><i className="fa-solid fa-arrow-up"></i></button>

            <header className="header__area-3">
                <div className="header__inner-3">
                    <div className="header__logo-2">
                        <Link to="/" className="logo-dark">
                            <img src="/assets/imgs/logo/sacesta-logo-black.png" style={{ width: '150px', height: '40px' }} alt="Site Logo" />
                        </Link>
                        <Link to="/" className="logo-light">
                            <img src="/assets/imgs/logo/site-logo-white-2.png" alt="Site Logo" />
                        </Link>
                    </div>
                    <div className="header__nav-2">
                        <ul className="main-menu-3 menu-anim">
                            <li><Link to="/">Home</Link></li>
                            <li><Link to="/about">About</Link></li>
                            <li><Link to="/our-work">Our Work</Link></li>
                            <li><a href="https://calendly.com/sacesta/30min">Book A Meet</a></li>
                        </ul>
                    </div>
                    <div className="header__nav-icon-3">
                        <button className="search-icon" id="search_icon"><i className="fa-solid fa-magnifying-glass"></i></button>
                        <button className="search-icon" id="search_close"><i className="fa-solid fa-xmark"></i></button>
                        <button id="open_offcanvas"><img src="/assets/imgs/icon/menu-black.png" alt="Menubar Icon" /></button>
                    </div>
                </div>
            </header>

            <div id="smooth-wrapper">
                <div id="smooth-content">

                    <div className="blog-detail-main">
                        <main>
                            <section className="blog-detail-hero">
                                <Link to={location.state?.from || '/our-work'} className="back-btn">
                                    <i className="fa-solid fa-arrow-left"></i>
                                </Link>
                                <div className="container">
                                    <h1 className="blog-detail-title">{blog.title}</h1>
                                </div>
                            </section>

                            <section className="blog-detail-content">
                                <div className="container">
                                    <div className="blog-cover-wrapper">
                                        <img src={`/${blog.thumbnail.image}`} alt={blog.thumbnail.alt} className="blog-detail-cover" />
                                        {blog.thumbnail.caption && <p className="cover-caption">{blog.thumbnail.caption}</p>}
                                    </div>

                                    <div className="blog-content-wrapper">
                                        <div className="blog-content">
                                            {renderContent(blog.content)}
                                        </div>

                                        {!blog.hideCTA && (
                                            <div className="blog-contact-btn-wrapper" data-scroll-ignore style={{ position: 'relative', zIndex: 100 }}>
                                                <a
                                                    href="https://www.linkedin.com/company/sacesta"
                                                    className="contact-rect-btn"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        window.open("https://calendly.com/sacesta/30min", "_blank");
                                                    }}
                                                >
                                                    {blog.tags.some((tag: string) => tag.toLowerCase().includes('plg')) ||
                                                        blog.title.toLowerCase().includes('plg')
                                                        ? 'Contact for PLG Consultation'
                                                        : blog.tags.some((tag: string) => tag.toLowerCase().includes('knowledge engineering')) ||
                                                            blog.title.toLowerCase().includes('knowledge engineering')
                                                            ? 'Contact for Consultation'
                                                            : blog.tags.some((tag: string) => tag.toLowerCase().includes('cliniko')) ||
                                                                blog.title.toLowerCase().includes('cliniko')
                                                                ? 'Contact for cliniko Consultation'
                                                                : blog.tags.some((tag: string) => tag.toLowerCase().includes('elevenlabs')) ||
                                                                    blog.title.toLowerCase().includes('elevenlabs')
                                                                    ? 'Contact for Elevenlabs Consultation'
                                                                    : blog.tags.some((tag: string) => tag.toLowerCase().includes('zapier')) ||
                                                                        blog.title.toLowerCase().includes('zapier')
                                                                        ? 'Contact for zapier Consultation'
                                                                        : blog.tags.some((tag: string) => tag.toLowerCase().includes('n8n')) ||
                                                                            blog.title.toLowerCase().includes('n8n')
                                                                            ? 'Contact for n8n Consultation'
                                                                            : blog.tags.some((tag: string) => tag.toLowerCase().includes('retell')) ||
                                                                                blog.title.toLowerCase().includes('retell')
                                                                                ? 'Contact for Retell Consultation'
                                                                                : blog.tags.some((tag: string) => tag.toLowerCase().includes('vitally')) ||
                                                                                    blog.title.toLowerCase().includes('vitally')
                                                                                    ? 'Contact for Vitally Consultation'
                                                                                    : blog.tags.some((tag: string) => tag.toLowerCase().includes('vibevoice') || tag.toLowerCase().includes('voice')) ||
                                                                                        blog.title.toLowerCase().includes('vibevoice') || blog.title.toLowerCase().includes('voice')
                                                                                        ? 'Contact for Voice AI Consultation'
                                                                                        : blog.tags.some((tag: string) => tag.toLowerCase().includes('rekognition') || tag.toLowerCase().includes('aws')) ||
                                                                                            blog.title.toLowerCase().includes('rekognition') || blog.title.toLowerCase().includes('aws')
                                                                                            ? 'Contact for AWS Consultation'
                                                                                            : (blog.tags.some((tag: string) => ['gdpr', 'hipaa', 'pci dss', 'ccpa'].includes(tag.toLowerCase())) ||
                                                                                                ['GDPR', 'HIPAA', 'PCI DSS', 'CCPA'].some(kw => blog.title.includes(kw)))
                                                                                                ? 'Contact for Consultation'
                                                                                                : blog.tags.some((tag: string) => tag.toLowerCase().includes('revops')) ||
                                                                                                    blog.title.toLowerCase().includes('revops')
                                                                                                    ? 'Contact for RevOps Consultation'
                                                                                                    : 'Contact for GHL Consultation'}
                                                    <i className="fa-solid fa-arrow-right"></i>
                                                </a>
                                            </div>
                                        )}

                                        <div className="blog-footer-meta">
                                            <div className="blog-tags">
                                                {blog.tags.map((tag: string, i: number) => (
                                                    <span key={i} className="tag">#{tag}</span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </main>
                        <MainFooter />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlogDetail;
